import {
  RadioButton,
  RadioGroup,
  Select,
  TextArea,
  TextField,
} from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";

import {
  Question,
  DisplayTypeEnum,
  DataTypeEnum,
} from "../../../../../models/Questionnaire/Question";

interface IProps {
  question: Question;
  value: string | number | null;
  onChange: (value: string | number | null) => void;
}

export const QuestionInput = ({ question, value, onChange }: IProps) => {
  const { t } = useTranslation();

  const handleTextFieldChange = (inputValue: string) => {
    if (question.dataTypeId === DataTypeEnum.Integer) {
      // Store as number in state, but pass as string to TextField
      onChange(inputValue ? Number(inputValue) : null);
    } else {
      onChange(inputValue || null);
    }
  };

  switch (question.displayTypeId) {
    case DisplayTypeEnum.Text:
    case DisplayTypeEnum.ReadOnly:
      return (
        <TextField
          name={`question-${question.questionGroupQuestionId}`}
          type={
            question.dataTypeId === DataTypeEnum.Integer ? "number" : "text"
          }
          value={value?.toString() ?? ""}
          label={question.name}
          disabled={question.displayTypeId === DisplayTypeEnum.ReadOnly}
          required={question.isRequired}
          maxLength={question.answerLength}
          onChange={(e) => handleTextFieldChange(e.target.value)}
          min={question.dataTypeId === DataTypeEnum.Integer ? 0 : undefined}
        />
      );

    case DisplayTypeEnum.TextArea:
      return (
        <TextArea
          name={`question-${question.questionGroupQuestionId}`}
          value={value?.toString() ?? ""}
          label={question.name}
          required={question.isRequired}
          maxLength={question.answerLength}
          rows={4}
          onChange={(e) => onChange(e.target.value || null)}
          ariaLabel={question.name}
        />
      );

    case DisplayTypeEnum.Select:
      return (
        <Select
          name={`question-${question.questionGroupQuestionId}`}
          value={value}
          label={question.name}
          required={question.isRequired}
          selectData={{
            valueProp: "id",
            textProp: "name",
            keyProp: "id",
            data: question.answers ?? [],
          }}
          onChange={(e) =>
            onChange(e.target.value ? Number(e.target.value) : null)
          }
        />
      );

    case DisplayTypeEnum.Radio:
      return (
        <RadioGroup
          name={`question-${question.questionGroupQuestionId}`}
          title={question.name}
          required={question.isRequired}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {question.answers?.map((answer) => (
            <RadioButton
              key={answer.id}
              label={answer.name}
              value={answer.id.toString()}
              checked={value === answer.id}
              ariaLabel={t("accessibility.radioAnswer")}
            />
          ))}
        </RadioGroup>
      );

    default:
      return null;
  }
};
